import React from "react"
import PropTypes from "prop-types"

interface KnobProps {
  value: number
  onChange: (value: number) => void
  onChangeEnd?: () => void
  min?: number
  max?: number
  step?: number
  log?: boolean
  width?: number
  height?: number
  thickness?: number
  lineCap?: "butt" | "round"
  bgColor?: string
  fgColor?: string
  inputColor?: string
  font?: string
  fontWeight?: string
  clockwise?: boolean
  cursor?: number | boolean
  stopper?: boolean
  readOnly?: boolean
  disableTextInput?: boolean
  displayInput?: boolean
  displayCustom?: (value: number) => string
  angleArc?: number
  angleOffset?: number
  disableMouseWheel?: boolean
  title?: string
  className?: string | null
  canvasClassName?: string | null
}

class Knob extends React.Component<KnobProps> {
  w: number = 200
  h: number = 200
  cursorExt: number = 0.3
  angleArc: number = 0
  angleOffset: number = 0
  startAngle: number = 0
  endAngle: number = 0
  digits: number = 2
  canvasRef: HTMLCanvasElement | null = null
  xy: number = 0
  lineWidth: number = 0
  radius: number = 0
  touchIndex: number = 0
  
  static propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onChangeEnd: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    log: PropTypes.bool,
    width: PropTypes.number,
    height: PropTypes.number,
    thickness: PropTypes.number,
    lineCap: PropTypes.oneOf(["butt", "round"]),
    bgColor: PropTypes.string,
    fgColor: PropTypes.string,
    inputColor: PropTypes.string,
    font: PropTypes.string,
    fontWeight: PropTypes.string,
    clockwise: PropTypes.bool,
    cursor: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    stopper: PropTypes.bool,
    readOnly: PropTypes.bool,
    disableTextInput: PropTypes.bool,
    displayInput: PropTypes.bool,
    displayCustom: PropTypes.func,
    angleArc: PropTypes.number,
    angleOffset: PropTypes.number,
    disableMouseWheel: PropTypes.bool,
    title: PropTypes.string,
    className: PropTypes.string,
    canvasClassName: PropTypes.string,
  }

  static defaultProps = {
    onChangeEnd: () => {},
    min: 0,
    max: 100,
    step: 1,
    log: false,
    width: 200,
    height: 200,
    thickness: 0.35,
    lineCap: "butt",
    bgColor: "#EEE",
    fgColor: "#EA2",
    inputColor: "",
    font: "Arial",
    fontWeight: "bold",
    clockwise: true,
    cursor: false,
    stopper: true,
    readOnly: false,
    disableTextInput: false,
    displayInput: true,
    angleArc: 360,
    angleOffset: 0,
    disableMouseWheel: false,
    className: null,
    canvasClassName: null,
  }

  constructor(props: KnobProps) {
    super(props)
    this.w = this.props.width || 200
    this.h = this.props.height || this.w
    this.cursorExt = this.props.cursor === true ? 0.3 : (typeof this.props.cursor === 'number' ? this.props.cursor / 100 : 0.3)
    this.angleArc = ((this.props.angleArc || 360) * Math.PI) / 180
    this.angleOffset = ((this.props.angleOffset || 0) * Math.PI) / 180
    this.startAngle = 1.5 * Math.PI + this.angleOffset
    this.endAngle = 1.5 * Math.PI + this.angleOffset + this.angleArc
    this.digits =
      Math.max(
        String(Math.abs(this.props.min || 0)).length,
        String(Math.abs(this.props.max || 100)).length,
        2
      ) + 2
  }

  componentDidMount() {
    this.drawCanvas()
    if (!this.props.readOnly && this.canvasRef) {
      this.canvasRef.addEventListener("touchstart", this.handleTouchStart, {
        passive: false,
      })
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: KnobProps) {
    if (nextProps.width && this.w !== nextProps.width) {
      this.w = nextProps.width
    }
    if (nextProps.height && this.h !== nextProps.height) {
      this.h = nextProps.height
    }
  }

  componentDidUpdate() {
    this.drawCanvas()
  }

  componentWillUnmount() {
    if (this.canvasRef) {
      this.canvasRef.removeEventListener("touchstart", this.handleTouchStart)
    }
  }

  getArcToValue = (v: number) => {
    let startAngle
    let endAngle
    const min = this.props.min ?? 0;
    const max = this.props.max ?? 100;
    const angle = !this.props.log
      ? ((v - min) * this.angleArc) /
        (max - min)
      : Math.log(Math.pow(v / min, this.angleArc)) /
        Math.log(max / min)
    if (!this.props.clockwise) {
      startAngle = this.endAngle + 0.00001
      endAngle = startAngle - angle - 0.00001
    } else {
      startAngle = this.startAngle - 0.00001
      endAngle = startAngle + angle + 0.00001
    }
    if (this.props.cursor) {
      startAngle = endAngle - this.cursorExt
      endAngle += this.cursorExt
    }
    return {
      startAngle,
      endAngle,
      acw: !this.props.clockwise && !this.props.cursor,
    }
  }

  // Calculate ratio to scale canvas to avoid blurriness on HiDPI devices
  getCanvasScale = (ctx: CanvasRenderingContext2D) => {
    const devicePixelRatio =
      window.devicePixelRatio ||
      ((window.screen as any).deviceXDPI / (window.screen as any).logicalXDPI) || // IE10
      1
    const backingStoreRatio = (ctx as any).webkitBackingStorePixelRatio || 1
    return devicePixelRatio / backingStoreRatio
  }

  coerceToStep = (v: number) => {
    const step = this.props.step ?? 1;
    const min = this.props.min ?? 0;
    const max = this.props.max ?? 100;
    let val = !this.props.log
      ? ~~((v < 0 ? -0.5 : 0.5) + v / step) * step
      : Math.pow(
          step,
          ~~(
            (Math.abs(v) < 1 ? -0.5 : 0.5) +
            Math.log(v) / Math.log(step)
          )
        )
    val = Math.max(Math.min(val, max), min)
    if (isNaN(val)) {
      val = 0
    }
    return Math.round(val * 1000) / 1000
  }

  eventToValue = (e: MouseEvent | Touch) => {
    if (!this.canvasRef) return 0;
    const min = this.props.min ?? 0;
    const max = this.props.max ?? 100;
    const bounds = this.canvasRef.getBoundingClientRect()
    const x = e.clientX - bounds.left
    const y = e.clientY - bounds.top
    let a = Math.atan2(x - this.w / 2, this.w / 2 - y) - this.angleOffset
    if (!this.props.clockwise) {
      a = this.angleArc - a - 2 * Math.PI
    }
    if (this.angleArc !== Math.PI * 2 && a < 0 && a > -0.5) {
      a = 0
    } else if (a < 0) {
      a += Math.PI * 2
    }
    const val = !this.props.log
      ? (a * (max - min)) / this.angleArc + min
      : Math.pow(max / min, a / this.angleArc) * min
    return this.coerceToStep(val)
  }

  handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    this.props.onChange(this.eventToValue(e.nativeEvent))
    document.addEventListener("mousemove", this.handleMouseMove)
    document.addEventListener("mouseup", this.handleMouseUp)
    document.addEventListener("keyup", this.handleEsc)
  }

  handleMouseMove = (e: MouseEvent) => {
    e.preventDefault()
    this.props.onChange(this.eventToValue(e))
  }

  handleMouseUp = (e?: MouseEvent) => {
    if (this.props.onChangeEnd) {
      this.props.onChangeEnd()
    }
    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)
    document.removeEventListener("keyup", this.handleEsc)
  }

  handleTouchStart = (e: TouchEvent) => {
    e.preventDefault()
    this.touchIndex = e.targetTouches.length - 1
    this.props.onChange(this.eventToValue(e.targetTouches[this.touchIndex]))
    document.addEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    })
    document.addEventListener("touchend", this.handleTouchEnd)
    document.addEventListener("touchcancel", this.handleTouchEnd)
  }

  handleTouchMove = (e: TouchEvent) => {
    e.preventDefault()
    this.props.onChange(this.eventToValue(e.targetTouches[this.touchIndex]))
  }

  handleTouchEnd = (e: TouchEvent) => {
    if (this.props.onChangeEnd) {
      this.props.onChangeEnd()
    }
    document.removeEventListener("touchmove", this.handleTouchMove)
    document.removeEventListener("touchend", this.handleTouchEnd)
    document.removeEventListener("touchcancel", this.handleTouchEnd)
  }

  handleEsc = (e: KeyboardEvent) => {
    if (e.keyCode === 27) {
      e.preventDefault()
      this.handleMouseUp()
    }
  }

  handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const min = this.props.min ?? 0;
    const max = this.props.max ?? 100;
    const val =
      Math.max(Math.min(+e.target.value, max), min) || min
    this.props.onChange(val)
  }

  handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const step = this.props.step ?? 1;
    if (e.deltaX > 0 || e.deltaY > 0) {
      this.props.onChange(
        this.coerceToStep(
          !this.props.log
            ? this.props.value + step
            : this.props.value * step
        )
      )
    } else if (e.deltaX < 0 || e.deltaY < 0) {
      this.props.onChange(
        this.coerceToStep(
          !this.props.log
            ? this.props.value - step
            : this.props.value / step
        )
      )
    }
  }

  handleArrowKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const step = this.props.step ?? 1;
    if (e.keyCode === 37 || e.keyCode === 40) {
      e.preventDefault()
      this.props.onChange(
        this.coerceToStep(
          !this.props.log
            ? this.props.value - step
            : this.props.value / step
        )
      )
    } else if (e.keyCode === 38 || e.keyCode === 39) {
      e.preventDefault()
      this.props.onChange(
        this.coerceToStep(
          !this.props.log
            ? this.props.value + step
            : this.props.value * step
        )
      )
    }
  }

  inputStyle = (): React.CSSProperties => ({
    width: `${(this.w / 2 + 4) >> 0}px`,
    height: `${(this.w / 3) >> 0}px`,
    position: "absolute" as const,
    verticalAlign: "middle" as const,
    marginTop: `${(this.w / 3) >> 0}px`,
    marginLeft: `-${((this.w * 3) / 4 + 2) >> 0}px`,
    border: 0,
    background: "none",
    font: `${this.props.fontWeight ?? "bold"} ${(this.w / this.digits) >> 0}px ${
      this.props.font ?? "Arial"
    }`,
    textAlign: "center" as const,
    color: this.props.inputColor || this.props.fgColor || "#EA2",
    padding: "0px",
    WebkitAppearance: "none" as any,
  })

  drawCanvas() {
    if (!this.canvasRef) return;
    const ctx = this.canvasRef.getContext("2d")
    if (!ctx) return;
    const scale = this.getCanvasScale(ctx)
    this.canvasRef.width = this.w * scale // clears the canvas
    this.canvasRef.height = this.h * scale
    ctx.scale(scale, scale)
    this.xy = this.w / 2 // coordinates of canvas center
    const thickness = this.props.thickness ?? 0.35;
    this.lineWidth = this.xy * thickness
    this.radius = this.xy - this.lineWidth / 2
    ctx.lineWidth = this.lineWidth
    ctx.lineCap = this.props.lineCap ?? "butt"
    // background arc
    ctx.beginPath()
    ctx.strokeStyle = this.props.bgColor ?? "#EEE"
    ctx.arc(
      this.xy,
      this.xy,
      this.radius,
      this.endAngle - 0.00001,
      this.startAngle + 0.00001,
      true
    )
    ctx.stroke()
    // foreground arc
    const a = this.getArcToValue(this.props.value)
    ctx.beginPath()
    ctx.strokeStyle = this.props.fgColor ?? "#EA2"
    ctx.arc(this.xy, this.xy, this.radius, a.startAngle, a.endAngle, a.acw)
    ctx.stroke()
  }

  renderCenter = () => {
    const {
      displayCustom,
      displayInput,
      disableTextInput,
      readOnly,
      value,
    } = this.props

    if (displayInput) {
      return (
        <input
          style={this.inputStyle()}
          type="text"
          value={value}
          onChange={this.handleTextInput}
          onKeyDown={this.handleArrowKey}
          readOnly={readOnly || disableTextInput}
        />
      )
    } else if (displayCustom && typeof displayCustom === "function") {
      return displayCustom(value)
    }
    return null
  }

  render() {
    const {
      canvasClassName,
      className,
      disableMouseWheel,
      readOnly,
      title,
      value,
    } = this.props

    return (
      <div
        className={className ?? undefined}
        style={{ width: this.w, height: this.h, display: "inline-block" }}
        onWheel={readOnly || disableMouseWheel ? undefined : this.handleWheel}
      >
        <canvas
          ref={ref => {
            this.canvasRef = ref
          }}
          className={canvasClassName ?? undefined}
          style={{ width: "100%", height: "100%" }}
          onMouseDown={readOnly ? undefined : this.handleMouseDown}
          title={title ? `${title}: ${value}` : String(value)}
        />
        {this.renderCenter()}
      </div>
    )
  }
}

export default Knob
